import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { hasFrontlineSession } from '@/auth/frontlineSession';

/**
 * Estado B de "Pantalla sin acceso a la plataforma" (ver guía de implementación, §4): el backend
 * RESPONDIÓ y dijo que esta persona no tiene ningún módulo habilitado. `profileStatus ===
 * 'resolved'` es la única prueba de que ese `false` es un dato y no un fallo — `'error'` y
 * `'pending'` devuelven exactamente los mismos flags (fail-soft de
 * `fetchCurrentUserProfileUncached`, api/client.ts), así que sin esta cláusula un 401 transitorio o
 * un reinicio de UserAdminService gatearía a un empleado legítimo.
 *
 * Dos exclusiones no son adorno:
 * - `hasFrontlineSession()` — RN-A06: un Frontline nunca tiene Pulso y siempre es usuario de
 *   Brillamos, ignorando los flags del DTO. Hay Frontlines con `CanViewBrillamos: false` en su
 *   ficha que hoy entran perfectamente.
 * - `visibleLinks.every(...)` — un `AdminBrillamos`/`AdminOUBrillamos` con `canViewBrillamos` en
 *   `false` sigue teniendo la vista `admin` (el bloque "Fundamentales" del panel se resuelve por
 *   permiso efectivo, no por módulo). `useVisibleNavLinks()` es la misma fuente que el riel y el
 *   nav móvil, así que no hace falta enumerar esos casos a mano.
 */
export function useNoPlatformAccess(): boolean {
  const currentUser = useCurrentUser();
  const { links: visibleLinks } = useVisibleNavLinks();

  if (currentUser?.profileStatus !== 'resolved') return false;
  if (currentUser.modules.canViewPulso || currentUser.modules.canViewBrillamos) return false;
  if (hasFrontlineSession()) return false;

  return visibleLinks.every((link) => link.view === 'fundamentals');
}
